import { Layer, Marker, Source } from "@vis.gl/react-maplibre";
import { FC } from "react";

import {
  DRAW_POLYLINE_OPACITY_INACTIVE,
  DRAW_POLYLINE_WEIGHT_ACTIVE,
  MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT,
} from "@ryde/controls";
import { TLatLngTuple } from "@ryde/shared";

import {
  MapLibreEditMidpointStyled,
  MapLibreEditVertexStyled,
} from "./maplibre-editable-polyline.styled";
import { useMapLibreEditablePolyline } from "./use-maplibre-editable-polyline";

interface IMapLibreEditablePolylineProps {
  positions: TLatLngTuple[];
  colorValue: string;
  isDeletePointMode: boolean;
  onChange: (positions: TLatLngTuple[]) => void;
}

/**
 * In-place polyline editor rendered with plain MapLibre layers/markers (the same
 * proven path the drawing mode uses): a colored line + draggable white vertex
 * handles + faded midpoint handles that insert a point. Delete-point mode turns
 * vertices into click-to-remove (guarded at `DRAW_POLYLINE_MIN_POINTS`).
 */
export const MapLibreEditablePolyline: FC<IMapLibreEditablePolylineProps> = ({
  positions,
  colorValue,
  isDeletePointMode,
  onChange,
}) => {
  const {
    editPositions,
    midpoints,
    lineFeature,
    hasDrawableLine,
    handleVertexDrag,
    handleVertexDelete,
    handleMidpointInsert,
  } = useMapLibreEditablePolyline({ positions, isDeletePointMode, onChange });

  return (
    <>
      {hasDrawableLine && (
        <Source id="maplibre-edit-polyline" type="geojson" data={lineFeature}>
          <Layer
            id="maplibre-edit-polyline-line"
            type="line"
            paint={{
              ["line-color"]: colorValue,
              ["line-width"]: DRAW_POLYLINE_WEIGHT_ACTIVE,
              ["line-opacity"]: DRAW_POLYLINE_OPACITY_INACTIVE,
            }}
          />
        </Source>
      )}

      {midpoints.map(mid => (
        <Marker
          key={`edit-mid-${mid.index}`}
          longitude={mid.lng}
          latitude={mid.lat}
          anchor="center"
          draggable
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
          onClick={event => {
            event.originalEvent.stopPropagation();
            handleMidpointInsert(mid.index, mid.lat, mid.lng);
          }}
          onDragEnd={event =>
            handleMidpointInsert(mid.index, event.lngLat.lat, event.lngLat.lng)
          }
        >
          <MapLibreEditMidpointStyled />
        </Marker>
      ))}

      {editPositions.map(([lat, lng], index) => (
        <Marker
          key={`edit-vertex-${index}`}
          longitude={lng}
          latitude={lat}
          anchor="center"
          draggable={!isDeletePointMode}
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
          onDrag={event =>
            handleVertexDrag(index, event.lngLat.lat, event.lngLat.lng, false)
          }
          onDragEnd={event =>
            handleVertexDrag(index, event.lngLat.lat, event.lngLat.lng, true)
          }
          onClick={
            isDeletePointMode
              ? event => {
                  event.originalEvent.stopPropagation();
                  handleVertexDelete(index);
                }
              : undefined
          }
        >
          <MapLibreEditVertexStyled $deletable={isDeletePointMode} />
        </Marker>
      ))}
    </>
  );
};
